import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useAuth } from '@/providers/auth-provider';

interface ProtectedRouteProps {
  children: ReactNode;
  // When true, only admins may see the page; approved non-admin heroes are
  // bounced to the home page (the API enforces this too — this is just UX).
  requireAdmin?: boolean;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// The single gate for authenticated pages. Redirect policy (one table):
//   loading                 → spinner
//   unauthenticated         → /login
//   pending / rejected      → /pending  (they haven't been approved for a mission)
//   approved/admin          → render (admin-only pages bounce non-admins to /)
export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { status, session } = useAuth();
  const navigate = useNavigate();

  const user = session?.user;
  const isApproved = user?.isAdmin || user?.status === 'approved';

  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate({ to: '/login' });
    } else if (status === 'authenticated' && user && !isApproved) {
      navigate({ to: '/pending' });
    } else if (status === 'authenticated' && requireAdmin && !user?.isAdmin) {
      navigate({ to: '/' });
    }
  }, [status, user, isApproved, requireAdmin, navigate]);

  if (status === 'loading') {
    return <Spinner />;
  }

  if (status === 'unauthenticated' || !session || !isApproved) {
    return null; // redirecting
  }

  if (requireAdmin && !user?.isAdmin) {
    return null; // redirecting to /
  }

  return <>{children}</>;
}
